/**
 * Resolve mission/supplication week copy for a course language.
 * Mirrors Unit.vue runtime behavior for PDF/export paths.
 */

export function normalizeLanguageCode(requiredLanguage) {
  const raw = (requiredLanguage || 'en').toLowerCase()
  if (raw.startsWith('pt')) return 'pt'
  if (raw === 'en') return 'en'
  return raw
}

export function localizeWeekForLanguage(week, requiredLanguage) {
  if (!week) return week

  const lang = normalizeLanguageCode(requiredLanguage)
  const lc =
    (week.localizedContent && week.localizedContent[lang]) ||
    (lang === 'pt' && week.pt ? week.pt : null)

  if (!lc) return week

  const merged = { ...week }

  const textFields = [
    'title',
    'subtitle',
    'missionBodyContent',
    'supplicationBodyContent',
    'supplicationreference',
    'supplicationverse',
  ]
  textFields.forEach((field) => {
    if (typeof lc[field] === 'string' && lc[field].trim() !== '') {
      merged[field] = lc[field]
    }
  })

  const arrayFields = [
    'missionStudyQuestions',
    'supplicationStudyQuestions',
    'supplicationVerseQuotes',
  ]
  arrayFields.forEach((field) => {
    if (Array.isArray(lc[field]) && lc[field].length > 0) {
      merged[field] = lc[field]
    }
  })

  return merged
}

export function stampWeekParagraphIds(weekItem, unitID) {
  if (!weekItem || !unitID) return weekItem

  const fieldAbbreviations = {
    missionBodyContent: 'mbc',
    supplicationBodyContent: 'sbc',
  }

  ;['missionBodyContent', 'supplicationBodyContent'].forEach((field) => {
    if (!weekItem[field] || typeof weekItem[field] !== 'string') return
    if (weekItem[field].includes(`id="${unitID}-`)) return

    const contentArray = weekItem[field].split('<p>')
    const abbreviation = fieldAbbreviations[field] || field
    weekItem[field] = contentArray
      .map(
        (content, index) => `<p id="${unitID}-${abbreviation}-${index}">${content}`
      )
      .join('')
  })

  return weekItem
}

export async function resolveLocalizedWeek(db, weekSource, requiredLanguage) {
  if (!weekSource) return null

  const lang = normalizeLanguageCode(requiredLanguage)
  let week = { ...weekSource }

  if (lang !== 'en' && week.id && db) {
    try {
      const snap = await db.collection('weeks').doc(week.id).get()
      if (snap.exists) {
        week = { ...week, ...snap.data(), id: snap.id }
      }
    } catch (error) {
      // Use course snapshot when Firestore is unavailable.
    }
  }

  const localized = localizeWeekForLanguage(week, lang)
  if (localized?.unitID) {
    stampWeekParagraphIds(localized, localized.unitID)
  } else if (weekSource.unitID) {
    stampWeekParagraphIds(localized, weekSource.unitID)
  }

  return localized
}

export function applyLocalizedWeekFields(targetUnit, localizedWeek, fallbackWeek) {
  if (!targetUnit) return
  const source = localizedWeek || fallbackWeek
  if (!source) return

  targetUnit.missionBodyContent = source.missionBodyContent
  targetUnit.missionStudyQuestions = source.missionStudyQuestions
  targetUnit.supplicationBodyContent = source.supplicationBodyContent
  targetUnit.supplicationStudyQuestions = source.supplicationStudyQuestions
  targetUnit.supplicationreference = source.supplicationreference
  targetUnit.supplicationverse = source.supplicationverse
  if (source.supplicationVerseQuotes) {
    targetUnit.supplicationVerseQuotes = source.supplicationVerseQuotes
  }
}
